import React, { useState, useContext } from "react";
import {
  TouchableOpacity,
  StyleSheet,
  useColorScheme,
  Dimensions,
  Platform,
  TextInput,
  ScrollView,
} from "react-native";
import { useForm, Controller } from "react-hook-form";
import { useToast } from "react-native-toast-notifications";
import { Picker } from "react-native";
import { useDispatch } from "react-redux";
import DeviceOrientationContext from "../../../../components/context/DeviceOrientationContext";
import { Text, View } from "../../../../components/shared/Themed";
import { AntDesign, Ionicons } from "@expo/vector-icons";
import { createOrganization } from "../../../../redux/dispatcher/adminDispatcher";
import * as ImagePicker from "expo-image-picker";
import FilePreview from "../../../Operations/utils/FilePreview";
import { pushFileToS3 } from "../../../../services/adminService";

type Props = {
  navigation: any;
};

// Form data type definition for company
type FormData = {
  name: string;
  description: string;
  logo: string;
  industry: string;
  size: string;
  website: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  contactInfo: {
    email: string;
    phone: string;
  };
  status: string;
};

const { width, height } = Dimensions.get("window");

// Company size options
const COMPANY_SIZES = [
  { label: "Startup", value: "startup" },
  { label: "Small", value: "small" },
  { label: "Medium", value: "medium" },
  { label: "Large", value: "large" },
  { label: "Enterprise", value: "enterprise" },
];

// Company status options
const COMPANY_STATUSES = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Suspended", value: "suspended" },
];

// Industry options (sample list)
const INDUSTRIES = [
  { label: "Technology", value: "Technology" },
  { label: "Finance", value: "Finance" },
  { label: "Healthcare", value: "Healthcare" },
  { label: "Manufacturing", value: "Manufacturing" },
  { label: "Transportation", value: "Transportation" },
  { label: "Retail", value: "Retail" },
  { label: "Education", value: "Education" },
  { label: "Food & Beverage", value: "Food & Beverage" },
  { label: "Construction", value: "Construction" },
  { label: "Hospitality", value: "Hospitality" },
  { label: "Other", value: "Other" },
];

export default function CreateAdminOrganizations({ navigation }: Props) {
  const colorScheme = useColorScheme();
  const { isSmallDevice } = useContext(DeviceOrientationContext);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch<any>();
  const toast = useToast();

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: "",
      description: "",
      logo: "",
      industry: "",
      size: "",
      website: "",
      address: {
        street: "",
        city: "",
        state: "",
        zipCode: "",
        country: "",
      },
      contactInfo: {
        email: "",
        phone: "",
      },
      status: "active",
    },
  });

  // Form submission handler
  const onSubmit = async (data: FormData) => {
    setLoading(true);
    if (file) {
      const uniqueId = new Date().getTime();
      const uniqueFileName = uniqueId + file.fileName;
      const s3UploadResponse = await pushFileToS3(
        "POST",
        file.fileUri,
        uniqueFileName,
        file.fileType,
        "company"
      );
      if (s3UploadResponse) {
        setValue("logo", uniqueFileName);
      }
    }
    // Get updated form values (logo included)
    const updatedData = getValues();

    dispatch(createOrganization({ requestBody: JSON.stringify(updatedData) }))
      .unwrap()
      .then(() => {
        setLoading(false);
        toast.show("Company created successfully", {
          type: "success",
        });
        navigation.reset({
          index: 0,
          routes: [
            {
              name: "AdminHome",
              params: { screen: "AdminOrganizations" },
            },
          ],
        });
      })
      .catch((err: any) => {
        setLoading(false);
        toast.show(err.message, {
          type: "danger",
        });
      });
  };

  // Form field component to reduce repetition
  const FormField = ({
    name,
    label,
    placeholder,
    rules = {},
  }: {
    name: string;
    label: string;
    placeholder: string;
    rules?: object;
  }) => {
    // Handle nested fields (for address and contactInfo)
    const isNested = name.includes(".");
    const [parent, child] = isNested ? name.split(".") : [name, ""];

    return (
      <>
        <Text style={styles.label}>{label}</Text>
        <Controller
          control={control}
          name={name as any}
          rules={rules}
          render={({ field: { onChange, onBlur, value } }) => (
            <TextInput
              style={[
                styles.input,
                isNested && errors[parent as keyof FormData]?.[child as any]
                  ? styles.inputError
                  : null,
                !isNested && errors[name as keyof FormData]
                  ? styles.inputError
                  : null,
              ]}
              onChangeText={onChange}
              onBlur={onBlur}
              value={isNested ? undefined : (value as string)}
              placeholder={placeholder}
            />
          )}
        />
        {isNested && errors[parent as keyof FormData]?.[child as any] && (
          <Text style={styles.errorText}>
            {
              errors[parent as keyof FormData]?.[child as any]
                ?.message as string
            }
          </Text>
        )}
        {!isNested && errors[name as keyof FormData] && (
          <Text style={styles.errorText}>
            {errors[name as keyof FormData]?.message as string}
          </Text>
        )}
      </>
    );
  };

  const [file, setFile] = useState<any>(null);
  const selectImage = async () => {
    // No permissions request is necessary for launching the image library
    let result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.All,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.3,
      base64: true,
      exif: false,
    });

    if (!result.canceled) {
      const fileObj = result.assets[0];
      await uploadImage(
        fileObj.fileName,
        fileObj.uri as string,
        fileObj.mimeType
      );
    }
  };

  const uploadImage = async (name: any, uri: string, mimeType: any) => {
    try {
      setFile({ fileName: name, fileUri: uri, fileType: mimeType });
    } catch (error) {
      console.error("Error fetching file: ", error);
      toast.show("Fetching File Failed", { type: "danger" });
    }
  };

  return (
    <ScrollView
      style={{
        height: "100%",
        backgroundColor: colorScheme === "light" ? "#fafafa" : "#fafafa",
      }}
    >
      {/* Form container */}
      <View style={styles.container}>
        <View style={styles.box}>
          <View style={{ paddingHorizontal: 15, paddingVertical: 5 }}>
            <TouchableOpacity
              onPress={() => navigation.navigate("AdminOrganizations")}
            >
              <AntDesign name="arrowleft" size={24} color="black" />
            </TouchableOpacity>
          </View>
          <Text style={styles.header}>Create New Company</Text>

          {/* Company Name Field */}
          <FormField
            name="name"
            label="Company Name"
            placeholder="Enter company name"
            rules={{ required: "Company name is required" }}
          />

          {/* Description Field */}
          <Text style={styles.label}>Description</Text>
          <Controller
            control={control}
            name="description"
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                style={[styles.textArea]}
                onChangeText={onChange}
                onBlur={onBlur}
                value={value}
                placeholder="Enter company description"
                multiline
                numberOfLines={4}
              />
            )}
          />

          {/* Logo URL Field */}
          <>
            <Text style={styles.label}>Logo</Text>
            <Controller
              name="logo"
              control={control}
              render={({ field: { onChange, value } }) => (
                <View>
                  {file ? (
                    <TouchableOpacity>
                      <FilePreview
                        key={1}
                        fileName={file.fileName}
                        fileUri={file.fileUri}
                        fileType={file.fileType}
                        styles={styles}
                        mode="preview"
                        hideIcons={true}
                        showName={false}
                        disableOpenFile={true}
                        openFileFunc={selectImage}
                      />
                    </TouchableOpacity>
                  ) : (
                    <TouchableOpacity
                      onPress={selectImage}
                      style={styles.uploadBox}
                    >
                      <Ionicons name="camera-outline" size={50} color="black" />
                      <Text style={styles.uploadText}>Upload Company Logo</Text>
                    </TouchableOpacity>
                  )}
                  {errors.logo && <Text>{errors.logo.message}</Text>}
                </View>
              )}
            />
          </>

          {/* Industry Field */}
          <Text style={styles.label}>Industry</Text>
          <Controller
            control={control}
            name="industry"
            rules={{ required: "Industry is required" }}
            render={({ field: { onChange, value } }) => (
              <Picker
                selectedValue={value}
                onValueChange={onChange}
                style={styles.picker}
              >
                <Picker.Item label="Select an industry" value="" />
                {INDUSTRIES.map((industry) => (
                  <Picker.Item
                    key={industry.value}
                    label={industry.label}
                    value={industry.value}
                  />
                ))}
              </Picker>
            )}
          />
          {errors.industry && (
            <Text style={styles.errorText}>{errors.industry.message}</Text>
          )}

          {/* Company Size Field */}
          <Text style={styles.label}>Company Size</Text>
          <Controller
            control={control}
            name="size"
            rules={{ required: "Company size is required" }}
            render={({ field: { onChange, value } }) => (
              <Picker
                selectedValue={value}
                onValueChange={onChange}
                style={styles.picker}
              >
                <Picker.Item label="Select company size" value="" />
                {COMPANY_SIZES.map((size) => (
                  <Picker.Item
                    key={size.value}
                    label={size.label}
                    value={size.value}
                  />
                ))}
              </Picker>
            )}
          />
          {errors.size && (
            <Text style={styles.errorText}>{errors.size.message}</Text>
          )}

          {/* Website Field */}
          <FormField
            name="website"
            label="Website"
            placeholder="Enter company website"
          />

          {/* Address Section */}
          <Text style={styles.sectionLabel}>Company Address</Text>

          <FormField
            name="address.street"
            label="Street"
            placeholder="Enter street address"
          />

          <FormField
            name="address.city"
            label="City"
            placeholder="Enter city"
          />

          <FormField
            name="address.state"
            label="State/Province"
            placeholder="Enter state or province"
          />

          <FormField
            name="address.zipCode"
            label="Zip/Postal Code"
            placeholder="Enter zip/postal code"
          />

          <FormField
            name="address.country"
            label="Country"
            placeholder="Enter country"
            rules={{ required: "Country is required" }}
          />

          {/* Contact Information Section */}
          <Text style={styles.sectionLabel}>Contact Information</Text>

          <FormField
            name="contactInfo.email"
            label="Contact Email"
            placeholder="Enter contact email"
            rules={{
              pattern: {
                value: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
                message: "Email is not valid",
              },
            }}
          />

          <FormField
            name="contactInfo.phone"
            label="Contact Phone"
            placeholder="Enter contact phone number"
          />

          {/* Status Field */}
          <Text style={styles.label}>Status</Text>
          <Controller
            control={control}
            name="status"
            render={({ field: { onChange, value } }) => (
              <Picker
                selectedValue={value}
                onValueChange={onChange}
                style={styles.picker}
              >
                {COMPANY_STATUSES.map((status) => (
                  <Picker.Item
                    key={status.value}
                    label={status.label}
                    value={status.value}
                  />
                ))}
              </Picker>
            )}
          />

          {/* Submit Button */}
          <TouchableOpacity
            style={styles.button}
            onPress={handleSubmit(onSubmit)}
            disabled={loading}
          >
            <Text style={styles.buttonText}>
              {loading ? "Creating..." : "Create Company"}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: width * 0.05,
    paddingVertical: height * 0.02,
    backgroundColor: "#f8f9fa",
    width: "100%",
    marginTop: 50,
    marginBottom: 50,
  },
  box: {
    width: "100%",
    maxWidth: 800,
    padding: 16,
    borderRadius: 12,
    backgroundColor: "#fff",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    fontSize: 22,
    fontWeight: "bold",
    marginBottom: 20,
    textAlign: "center",
    color: "#333",
  },
  sectionLabel: {
    fontSize: 18,
    fontWeight: "bold",
    marginTop: 20,
    marginBottom: 12,
    color: "#333",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    paddingBottom: 8,
  },
  label: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 8,
    color: "#555",
  },
  input: {
    width: "100%",
    height: 50,
    backgroundColor: "#fff",
    paddingHorizontal: 16,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 8,
    marginBottom: 12,
    fontSize: 16,
    color: "#333",
    ...Platform.select({
      ios: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  textArea: {
    width: "100%",
    height: 100,
    backgroundColor: "#fff",
    paddingHorizontal: 16,
    paddingTop: 12,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 8,
    marginBottom: 12,
    fontSize: 16,
    color: "#333",
    textAlignVertical: "top",
    ...Platform.select({
      ios: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  inputError: {
    borderColor: "red",
    backgroundColor: "#ffe6e6",
  },
  errorText: {
    color: "red",
    fontSize: 14,
    marginBottom: 8,
    textAlign: "left",
  },
  button: {
    alignItems: "center",
    justifyContent: "center",
    height: 50,
    backgroundColor: "#1E90FF",
    borderRadius: 8,
    marginTop: 20,
    width: "100%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
  },
  buttonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 18,
  },
  picker: {
    paddingVertical: 10,
    paddingHorizontal: 15,
    fontSize: 15,
    marginTop: 10,
    marginBottom: 10,
  },
  uploadBox: {
    width: "100%", // Adjust based on screen width
    height: width < 500 ? 163 : 233,
    backgroundColor: "#fff",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "gray",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 30,
    marginTop: 30,
  },
  uploadText: {
    marginTop: 10,
    fontSize: 16,
  },

  imagePreview: {
    width: "100%",
    height: width < 500 ? 163 : 233,
    borderWidth: 1,
    marginBottom: 20,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    objectFit: "contain",
    opacity: 0.6,
  },
});
